import { useState, useRef, useCallback, useEffect, useLayoutEffect, MutableRefObject } from "react";

interface UseElapsedTimeProps {
    isPlaying: boolean;
    options: {
        duration: number;
        onComplete: VoidFunction;
        startAt: number;
    };
}

export const useElapsedTime = (props: UseElapsedTimeProps): { elapsedTime: number } => {
    const { isPlaying, options } = props;
    const { duration, startAt = 0 } = options;

    const [elapsedTime, setElapsedTime] = useState(startAt);
    const requestRef: MutableRefObject<number | null> = useRef<number | null>(null);
    const previousTimeRef: MutableRefObject<number | null> = useRef<number | null>(null);
    const isMountedRef = useRef<boolean>(false);
    const isCompletedRef = useRef<boolean>(false);

    const loop = useCallback(
        (time: number): void => {
            const timeSec = time / 1000;
            if (previousTimeRef.current === null) {
                previousTimeRef.current = timeSec;
                requestRef.current = requestAnimationFrame(loop);
                return;
            }

            const deltaTime = timeSec - previousTimeRef.current;
            previousTimeRef.current = timeSec;

            setElapsedTime(prevTime => {
                const currentElapsedTime = prevTime + deltaTime;
                if (typeof duration !== "number" || currentElapsedTime < duration) {
                    return currentElapsedTime;
                }

                // Time is completed
                isCompletedRef.current = true;
                return duration;
            });

            if (!isCompletedRef.current) {
                requestRef.current = requestAnimationFrame(loop);
            } else {
                options.onComplete();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [duration],
    );

    // clear on complete
    const cleanup = useCallback((): void => {
        requestRef.current && cancelAnimationFrame(requestRef.current);
        previousTimeRef.current = null;
    }, []);

    useEffect(() => {
        if (isPlaying) {
            requestRef.current = requestAnimationFrame(loop);
        }

        return cleanup;
    }, [isPlaying, loop, cleanup]);

    // update on duration change
    useLayoutEffect(() => {
        if (isPlaying && isMountedRef.current) {
            cleanup();
            requestRef.current = requestAnimationFrame(loop);
        }
    }, [duration, cleanup, loop, isPlaying]);

    useLayoutEffect(() => {
        isMountedRef.current = true;
        return (): void => {
            isMountedRef.current = false;
        };
    });

    return { elapsedTime };
};
